import { useState, type FormEvent } from 'react';
import { useAuth } from '../../lib/auth';
import { ApiError } from '../../lib/api';

/**
 * 登录页。
 *
 * 未登录时渲染它，替代整个应用主体 —— 早期做法是照常渲染再各自弹 401，
 * 页面会同时闪着行情卡片和「请登录」，体验割裂。
 */
export function LoginView() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await login(username.trim(), password);
      // 成功后不跳转：AuthProvider 会切换渲染到主应用
    } catch (err) {
      // 不区分「用户不存在」与「密码错误」—— 服务端已统一措辞
      setError(err instanceof ApiError ? err.message : '登录失败，请稍后重试');
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center px-4"
      style={{ background: 'var(--bg-base)' }}>
      <div className="w-full max-w-[340px]">
        <div className="mb-6 text-center">
          <div
            className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-[10px] text-[15px] font-bold"
            style={{ background: 'var(--accent)', color: '#fff' }}
            aria-hidden
          >
            股
          </div>
          <h1 className="text-[17px] font-semibold">股记</h1>
          <p className="mt-1 text-[12px]" style={{ color: 'var(--text-3)' }}>
            一根 K 线 = 一篇投资日记
          </p>
        </div>

        <form
          onSubmit={submit}
          className="rounded-[10px] border p-5"
          style={{ background: 'var(--bg-surface)', boxShadow: 'var(--shadow-card)' }}
        >
          <label className="mb-3 block">
            <span className="mb-1 block text-[12px]" style={{ color: 'var(--text-2)' }}>用户名</span>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              className="w-full rounded-[6px] border px-2.5 py-2 text-[14px] outline-none"
              style={{ background: 'var(--bg-inset)', color: 'var(--text-1)' }}
            />
          </label>

          <label className="mb-4 block">
            <span className="mb-1 block text-[12px]" style={{ color: 'var(--text-2)' }}>密码</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="w-full rounded-[6px] border px-2.5 py-2 text-[14px] outline-none"
              style={{ background: 'var(--bg-inset)', color: 'var(--text-1)' }}
            />
          </label>

          {error && (
            <div className="mb-3 rounded-[6px] px-2.5 py-2 text-[12px]"
              style={{ background: 'var(--bg-elevated)', color: 'var(--warn)' }}
              role="alert">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !username.trim() || !password}
            className="w-full rounded-[6px] py-2 text-[14px] font-medium disabled:opacity-50"
            style={{ background: 'var(--accent)', color: '#fff' }}
          >
            {busy ? '登录中…' : '登录'}
          </button>
        </form>

        <p className="mt-4 text-center text-[11px] leading-[1.6]" style={{ color: 'var(--text-3)' }}>
          账号由管理员创建，不开放自助注册。
          <br />
          如需开通请联系管理员执行
          <code className="mx-1 rounded px-1" style={{ background: 'var(--bg-elevated)' }}>
            cd server-go &amp;&amp; ./run.sh create-user --username &lt;名&gt; --password &lt;口令&gt;
          </code>
        </p>
      </div>
    </div>
  );
}
